import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAppContext } from '../../App';
import { api } from '../../apiService';
import { toast } from 'react-toastify';
import ConfirmationModal from '../Common/ConfirmationModal';

// --- Internal Component: UserEditModal ---
function UserEditModal({ isOpen, onClose, userToEdit, allStations, onSave, availablePagesForStaff, commonPages, mixfmOnlyPages, flareOnlyPages }) {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [role, setRole] = useState('');
    const [assignedStations, setAssignedStations] = useState([]);
    const [allowedPages, setAllowedPages] = useState([]);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (userToEdit) {
            setName(userToEdit.name || '');
            setEmail(userToEdit.email || '');
            setRole(userToEdit.role || 'staff');
            setAssignedStations(userToEdit.assignedStations || []);
            setAllowedPages(userToEdit.allowedPages || []);
            setNewPassword('');
            setConfirmPassword('');
        }
    }, [userToEdit]);

    if (!isOpen || !userToEdit) return null;

    const handleStationToggle = (stationId) => {
        setAssignedStations(prev => prev.includes(stationId) ? prev.filter(id => id !== stationId) : [...prev, stationId]);
    };
    
    const handlePageToggle = (pageName) => {
        setAllowedPages(prev => prev.includes(pageName) ? prev.filter(p => p !== pageName) : [...prev, pageName]);
    };

    const handleSave = async () => {
        if (!email) {
            toast.error("Email is required.");
            return;
        }
        if (newPassword && newPassword !== confirmPassword) {
            toast.error("Password confirmation does not match.");
            return;
        }
        setIsSaving(true);
        const payload = {
            name,
            email,
            role,
            assignedStations: role === 'admin' ? allStations.map(s => s.id) : assignedStations,
            allowedPages: role === 'admin' ? availablePagesForStaff : allowedPages,
        };
        if (newPassword) payload.password = newPassword;
        await onSave(userToEdit.id, payload);
        setIsSaving(false);
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-2xl">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Edit User</h2>
                <p className="text-gray-500 mb-6 font-mono">{userToEdit.email}</p>
                <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Name</label>
                            <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full p-3 bg-gray-50 rounded-md" placeholder="Full Name" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Email</label>
                            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full p-3 bg-gray-50 rounded-md" placeholder="email@example.com" required />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">New Password</label>
                            <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="mt-1 w-full p-3 bg-gray-50 rounded-md" placeholder="Leave blank to keep current" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Confirm Password</label>
                            <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="mt-1 w-full p-3 bg-gray-50 rounded-md" placeholder="Confirm new password" disabled={!newPassword} />
                        </div>
                    </div>
                    <div>
                        <label htmlFor="role" className="block text-sm font-medium text-gray-700">Role</label>
                        <select id="role" value={role} onChange={(e) => setRole(e.target.value)} className="mt-1 w-full p-3 bg-gray-50 rounded-md">
                            <option value="staff">Staff</option>
                            <option value="admin">Admin</option>
                        </select>
                    </div>
                    {role === 'staff' && (
                        <>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Assigned Stations</label>
                                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 border rounded-md bg-gray-50">
                                    {allStations.map(station => (
                                        <label key={station.id} className="flex items-center space-x-3 p-3 bg-white border rounded-md hover:bg-gray-100 cursor-pointer">
                                            <input type="checkbox" checked={assignedStations.includes(station.id)} onChange={() => handleStationToggle(station.id)} className="h-4 w-4 text-rose-600 border-gray-300 rounded focus:ring-rose-500" />
                                            <span className="text-sm font-medium text-gray-700">{station.name}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Page Access Permissions</label>
                                <div className="space-y-3 max-h-60 overflow-y-auto p-2 border rounded-md bg-gray-50">
                                    <div>
                                        <div className="text-xs font-bold text-slate-600 mb-2">Common Pages</div>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                            {commonPages.map(page => (
                                                <label key={page} className="flex items-center space-x-3 p-3 bg-white border rounded-md hover:bg-gray-100 cursor-pointer">
                                                    <input type="checkbox" checked={allowedPages.includes(page)} onChange={() => handlePageToggle(page)} className="h-4 w-4 text-rose-600 border-gray-300 rounded focus:ring-rose-500" />
                                                    <span className="text-sm font-medium text-gray-700 capitalize">{page.replace(/([A-Z])/g, ' $1').trim()}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-xs font-bold text-slate-600 mb-2">Mix FM Only</div>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                            {mixfmOnlyPages.map(page => (
                                                <label key={page} className="flex items-center space-x-3 p-3 bg-white border rounded-md hover:bg-gray-100 cursor-pointer">
                                                    <input type="checkbox" checked={allowedPages.includes(page)} onChange={() => handlePageToggle(page)} className="h-4 w-4 text-rose-600 border-gray-300 rounded focus:ring-rose-500" />
                                                    <span className="text-sm font-medium text-gray-700 capitalize">{page.replace(/([A-Z])/g, ' $1').trim()}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-xs font-bold text-slate-600 mb-2">Flare Only</div>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                            {flareOnlyPages.map(page => (
                                                <label key={page} className="flex items-center space-x-3 p-3 bg-white border rounded-md hover:bg-gray-100 cursor-pointer">
                                                    <input type="checkbox" checked={allowedPages.includes(page)} onChange={() => handlePageToggle(page)} className="h-4 w-4 text-rose-600 border-gray-300 rounded focus:ring-rose-500" />
                                                    <span className="text-sm font-medium text-gray-700 capitalize">{page.replace(/([A-Z])/g, ' $1').trim()}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
                <div className="flex justify-end gap-4 mt-8">
                    <button onClick={onClose} className="px-5 py-2 bg-gray-300 text-gray-800 rounded-lg font-semibold">Cancel</button>
                    <button onClick={handleSave} disabled={isSaving} className="px-5 py-2 bg-rose-600 text-white rounded-lg font-semibold disabled:bg-gray-400">{isSaving ? 'Saving...' : 'Save Changes'}</button>
                </div>
            </div>
        </div>
    );
}


function UserManagementSection() {
    const { user, allStations } = useAppContext();
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [deletingUser, setDeletingUser] = useState(null);
    const [editingUser, setEditingUser] = useState(null);
    const [newUserName, setNewUserName] = useState('');
    const [newUserEmail, setNewUserEmail] = useState('');
    const [newUserPassword, setNewUserPassword] = useState('');
    const [newUserRole, setNewUserRole] = useState('staff');
    const [newUserAssignedStations, setNewUserAssignedStations] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [newUserAllowedPages, setNewUserAllowedPages] = useState([]);
    
    const radioStations = useMemo(() => {
        return (allStations || []).filter(s => {
            const frequency = (s.frequency || '').trim().toUpperCase();
            const city = (s.city || '').trim().toUpperCase();
            return Boolean(s.id) && frequency && frequency !== 'N/A' && city !== 'N/A' && !(s.name || '').toLowerCase().includes('flare');
        });
    }, [allStations]);

    const flareBusiness = useMemo(() => {
        return (allStations || []).find(s => {
            const name = (s.name || '').toLowerCase();
            const frequency = (s.frequency || '').trim().toUpperCase();
            return name.includes('flare') || frequency === 'N/A';
        }) || null;
    }, [allStations]);

    const mixfmPages = useMemo(() => ([
        'dashboard', 'clients', 'adCampaigns', 'adLogs', 'invoices', 'payments', 'expenses', 'billManagement',
        'liveKiosk', 'monthlyReport', 'employeeRegistration', 'salaryManagement'
    ]), []);
    
    const flarePages = useMemo(() => ([
        'dashboard', 'magazineSales', 'clients',
        'invoices', 'payments', 'expenses', 'billManagement', 'liveKiosk', 'monthlyReport', 'employeeRegistration', 'salaryManagement'
    ]), []);

    const commonPages = useMemo(() => {
        const flareSet = new Set(flarePages);
        return mixfmPages.filter(p => flareSet.has(p));
    }, [mixfmPages, flarePages]);
    
    const mixfmOnlyPages = useMemo(() => {
        const flareSet = new Set(flarePages);
        return mixfmPages.filter(p => !flareSet.has(p));
    }, [mixfmPages, flarePages]);
    
    const flareOnlyPages = useMemo(() => {
        const mixSet = new Set(mixfmPages);
        return flarePages.filter(p => !mixSet.has(p));
    }, [mixfmPages, flarePages]);

    const availablePagesForStaff = useMemo(() => {
        const set = new Set([...mixfmPages, ...flarePages]);
        return Array.from(set);
    }, [mixfmPages, flarePages]);

    const fetchUsers = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await api.get('users');
            setUsers(data);
            const updates = (data || [])
                .filter(u => u.role === 'staff' && Array.isArray(u.allowedPages))
                .map(u => {
                    const sanitized = u.allowedPages.filter(p => availablePagesForStaff.includes(p));
                    if (sanitized.length === u.allowedPages.length) return null;
                    return { id: u.id, allowedPages: sanitized };
                })
                .filter(Boolean);
            if (updates.length > 0) {
                await Promise.all(updates.map(u => api.put('users', u.id, { allowedPages: u.allowedPages })));
            }
        } catch (err) {
            toast.error(`Error fetching user list: ${err.message}`);
        } finally {
            setIsLoading(false);
        }
    }, [availablePagesForStaff]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);
    
    const resetNewUserForm = () => {
        setNewUserName(''); setNewUserEmail(''); setNewUserPassword(''); setNewUserAssignedStations([]);
        setNewUserRole('staff'); setNewUserAllowedPages([]);
    };
    
    const handleAddUser = async (e) => {
        e.preventDefault();
        if (newUserRole === 'staff' && newUserAssignedStations.length === 0) {
            toast.error("Staff users must be assigned to at least one station."); return;
        }
        if (newUserRole === 'staff' && newUserAllowedPages.length === 0) {
            toast.error("Please select at least one page permission."); return;
        }
        setIsSubmitting(true);

        try {
            const allowedPagesSanitized = newUserAllowedPages.filter(p => availablePagesForStaff.includes(p));
            const userProfileData = {
                name: newUserName,
                email: newUserEmail, 
                password: newUserPassword, // Password will be hashed on the server
                role: newUserRole,
                assignedStations: newUserRole === 'staff' ? newUserAssignedStations : allStations.map(s => s.id),
                allowedPages: newUserRole === 'admin' ? availablePagesForStaff : allowedPagesSanitized,
            };
            await api.post('users', userProfileData);
            toast.success(`User ${newUserEmail} created successfully.`);
            resetNewUserForm();
            fetchUsers();
        } catch (err) {
            toast.error(`Failed to create user: ${err.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteUser = async () => {
        if (!deletingUser) return;
        try {
            await api.delete('users', deletingUser.id);
            toast.success(`User profile for ${deletingUser.email} deleted.`);
            fetchUsers();
        } catch (err) {
            toast.error(`Failed to delete user profile: ${err.message}`);
        } finally {
            setDeletingUser(null);
        }
    };
    
    const handleUpdateUser = async (id, updatedData) => {
        try {
            const sanitized = {
                ...updatedData,
                allowedPages: Array.isArray(updatedData.allowedPages)
                    ? updatedData.allowedPages.filter(p => availablePagesForStaff.includes(p))
                    : updatedData.allowedPages
            };
            await api.put('users', id, sanitized);
            toast.success("User profile and permissions updated successfully!");
            fetchUsers();
        } catch (error) {
            toast.error(`Failed to update user profile: ${error.message}`);
        }
    };

    const handleStationToggle = (stationId) => setNewUserAssignedStations(prev => prev.includes(stationId) ? prev.filter(id => id !== stationId) : [...prev, stationId]);
    const handleNewUserPageToggle = (pageName) => setNewUserAllowedPages(prev => prev.includes(pageName) ? prev.filter(page => page !== pageName) : [...prev, pageName]);
    
    return (
        <div className="container mx-auto">
            <ConfirmationModal isOpen={!!deletingUser} onClose={() => setDeletingUser(null)} onConfirm={handleDeleteUser} title="Delete User Profile">
                Are you sure you want to delete the profile for <strong>{deletingUser?.email}</strong>? This action is permanent.
            </ConfirmationModal>
            <UserEditModal isOpen={!!editingUser} onClose={() => setEditingUser(null)} userToEdit={editingUser} allStations={allStations} onSave={handleUpdateUser} availablePagesForStaff={availablePagesForStaff} commonPages={commonPages} mixfmOnlyPages={mixfmOnlyPages} flareOnlyPages={flareOnlyPages} />
            <div className="mb-8"><h1 className="text-4xl font-bold text-gray-800">User Management</h1><p className="text-gray-500 mt-1">Add, view, and manage application users and their roles.</p></div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                <div className="lg:col-span-1">
                    <div className="bg-white rounded-2xl shadow-lg p-8 sticky top-8">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6">Add New User</h2>
                        <form onSubmit={handleAddUser} className="space-y-6">
                            <div><label className="block text-sm font-medium text-gray-700">Name</label><input type="text" value={newUserName} onChange={e => setNewUserName(e.target.value)} className="mt-1 w-full p-3 bg-gray-50 rounded-md" placeholder="Full Name" /></div>
                            <div><label className="block text-sm font-medium text-gray-700">Email Address</label><input type="email" value={newUserEmail} onChange={e => setNewUserEmail(e.target.value)} className="mt-1 w-full p-3 bg-gray-50 rounded-md" required /></div>
                            <div><label className="block text-sm font-medium text-gray-700">Set Password</label><input type="password" value={newUserPassword} onChange={e => setNewUserPassword(e.target.value)} className="mt-1 w-full p-3 bg-gray-50 rounded-md" required /></div>
                            <div><label className="block text-sm font-medium text-gray-700">Role</label><select value={newUserRole} onChange={e => setNewUserRole(e.target.value)} className="mt-1 w-full p-3 bg-gray-50 rounded-md"><option value="staff">Staff</option><option value="admin">Admin</option></select></div>
                            {newUserRole === 'staff' && (
                                <>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Assign Stations</label>
                                        <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto pr-2">
                                            {radioStations.map(station => (<label key={station.id} className="flex items-center space-x-3 p-3 border rounded-md hover:bg-gray-50 cursor-pointer"><input type="checkbox" checked={newUserAssignedStations.includes(station.id)} onChange={() => handleStationToggle(station.id)} className="h-4 w-4 text-rose-600 border-gray-300 rounded focus:ring-rose-500" /><span className="text-sm font-medium text-gray-700">{station.name}</span></label>))}
                                            {flareBusiness && (<label key={flareBusiness.id} className="flex items-center space-x-3 p-3 border rounded-md hover:bg-gray-50 cursor-pointer bg-slate-50"><input type="checkbox" checked={newUserAssignedStations.includes(flareBusiness.id)} onChange={() => handleStationToggle(flareBusiness.id)} className="h-4 w-4 text-rose-600 border-gray-300 rounded focus:ring-rose-500" /><span className="text-sm font-medium text-gray-700">{flareBusiness.name || 'Flare Magazine'}</span></label>)}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Set Page Access</label>
                                        <div className="space-y-3 max-h-60 overflow-y-auto p-2 border rounded-md bg-gray-50">
                                            <div>
                                                <div className="text-xs font-bold text-slate-600 mb-2">Common Pages</div>
                                                <div className="grid grid-cols-2 gap-2">
                                                    {commonPages.map(page => (<label key={page} className="flex items-center space-x-3 p-2 bg-white border rounded-md hover:bg-gray-100 cursor-pointer"><input type="checkbox" checked={newUserAllowedPages.includes(page)} onChange={() => handleNewUserPageToggle(page)} className="h-4 w-4 text-rose-600 border-gray-300 rounded focus:ring-rose-500" /><span className="text-xs font-medium text-gray-700 capitalize">{page.replace(/([A-Z])/g, ' $1').trim()}</span></label>))}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="text-xs font-bold text-slate-600 mb-2">Mix FM Only</div>
                                                <div className="grid grid-cols-2 gap-2">
                                                    {mixfmOnlyPages.map(page => (<label key={page} className="flex items-center space-x-3 p-2 bg-white border rounded-md hover:bg-gray-100 cursor-pointer"><input type="checkbox" checked={newUserAllowedPages.includes(page)} onChange={() => handleNewUserPageToggle(page)} className="h-4 w-4 text-rose-600 border-gray-300 rounded focus:ring-rose-500" /><span className="text-xs font-medium text-gray-700 capitalize">{page.replace(/([A-Z])/g, ' $1').trim()}</span></label>))}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="text-xs font-bold text-slate-600 mb-2">Flare Only</div>
                                                <div className="grid grid-cols-2 gap-2">
                                                    {flareOnlyPages.map(page => (<label key={page} className="flex items-center space-x-3 p-2 bg-white border rounded-md hover:bg-gray-100 cursor-pointer"><input type="checkbox" checked={newUserAllowedPages.includes(page)} onChange={() => handleNewUserPageToggle(page)} className="h-4 w-4 text-rose-600 border-gray-300 rounded focus:ring-rose-500" /><span className="text-xs font-medium text-gray-700 capitalize">{page.replace(/([A-Z])/g, ' $1').trim()}</span></label>))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                            <button type="submit" disabled={isSubmitting} className="w-full bg-rose-600 text-white font-bold py-4 rounded-lg shadow-lg hover:bg-rose-700 transition-all disabled:bg-gray-400">{isSubmitting ? "Adding..." : "Add User"}</button>
                        </form>
                    </div>
                </div>
                <div className="lg:col-span-2 bg-white rounded-2xl shadow-lg overflow-hidden">
                    <div className="p-6 border-b"><h2 className="text-2xl font-bold text-gray-800">Existing Users</h2></div>
                    <div className="overflow-x-auto">
                        {isLoading ? <p className="p-8 text-center">Loading users...</p> : (
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Assigned Stations</th>
                                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {users.length > 0 ? users.map(u => (
                                        <tr key={u.id}>
                                            <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm font-medium text-gray-900">{u.name || '—'}</div><div className="text-xs text-gray-500 font-mono">{u.email}</div></td>
                                            <td className="px-6 py-4 whitespace-nowrap"><span className={`capitalize px-2 py-1 text-xs font-semibold rounded-full ${u.role === 'admin' ? 'bg-rose-100 text-rose-800' : 'bg-gray-100 text-gray-800'}`}>{u.role}</span></td>
                                            <td className="px-6 py-4 text-sm text-gray-500 hidden md:table-cell">{u.role === 'admin' ? 'All' : (u.assignedStations || []).map(sid => allStations.find(s => s.id === sid)?.name || sid).join(', ')}</td>
                                            <td className="px-6 py-4 text-center whitespace-nowrap text-sm font-medium">{user.id !== u.id && (<><button onClick={() => setEditingUser(u)} className="text-blue-600 hover:text-blue-800">Edit</button><span className="text-gray-300 mx-2">|</span><button onClick={() => setDeletingUser(u)} className="text-red-600 hover:text-red-800">Delete</button></>)}</td>
                                        </tr>
                                    )) : (<tr><td colSpan="4" className="text-center p-8">No other users found.</td></tr>)}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default UserManagementSection;

